/**
 * Mock Authentication Service
 * 
 * Provides fake authentication for local development.
 * Generates realistic JWT tokens without requiring a real auth provider.
 * 
 * USE CASE: Early development, testing MFE integration, offline work
 * 
 * IMPORTANT: This service should NEVER be used in production.
 * Use REACT_APP_AUTH_MODE=mock to enable this service.
 * 
 * @module services/auth/MockAuthService
 */

import { AuthTokens, IAuthService } from '@/types/auth';
import { CommunicationBridge } from '@sequelae/lore-mfe-kit';

export class MockAuthService implements IAuthService {
  private static readonly STORAGE_KEY = 'mock_auth_token';
  private static readonly USER_ID_KEY = 'mock_user_id';

  /**
   * Mock login - accepts any email/password
   * 
   * @param email - User email (stored for display purposes)
   * @param password - Password (not validated)
   * @returns Mock JWT token
   */
  async login(email: string, _password: string): Promise<AuthTokens> {
    console.log('🎭 [MockAuth] Login attempt:', email);

    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));

    // Generate mock user ID (use email hash for consistency)
    const mockUserId = this.generateMockUserId(email);

    // Create realistic JWT structure
    const mockToken = this.generateMockToken(mockUserId, email);

    // Store token
    this.storeToken(mockToken, mockUserId);

    console.log('✅ [MockAuth] Login successful:', {
      userId: mockUserId,
      email,
      tokenLength: mockToken.length,
    });

    return {
      accessToken: mockToken,
      expiresIn: 3600, // 1 hour
    };
  }

  /**
   * Mock login with token - validates format only
   * 
   * @param token - JWT token string
   * @returns Token wrapped in AuthTokens
   */
  async loginWithToken(token: string): Promise<AuthTokens> {
    console.log('🎭 [MockAuth] Login with token');

    // Basic validation
    if (!token || token.split('.').length !== 3) {
      throw new Error('Invalid token format');
    }

    // Extract user ID from token
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const userId = payload.ref_user_id || '12345';
      
      this.storeToken(token, userId);

      console.log('✅ [MockAuth] Token login successful:', { userId });

      return {
        accessToken: token,
      };
    } catch (error) {
      throw new Error('Failed to parse token');
    }
  }

  /**
   * Mock logout - clears stored token
   */
  async logout(): Promise<void> {
    console.log('🎭 [MockAuth] Logout');
    
    localStorage.removeItem(MockAuthService.STORAGE_KEY);
    localStorage.removeItem(MockAuthService.USER_ID_KEY);
    CommunicationBridge.setAccessToken('');
  }

  /**
   * Get current access token
   */
  async getAccessToken(): Promise<string | null> {
    return localStorage.getItem(MockAuthService.STORAGE_KEY);
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem(MockAuthService.STORAGE_KEY);
    return !!token;
  }

  /**
   * Generate mock JWT token
   * 
   * Creates a realistic JWT structure with:
   * - Standard header (alg, typ)
   * - Payload with ref_user_id, email, exp, iat, iss, aud
   * - Mock signature
   * 
   * @param userId - Mock user ID
   * @param email - User email
   * @returns JWT token string
   */
  private generateMockToken(userId: string, email: string): string {
    const now = Math.floor(Date.now() / 1000);

    const header = {
      alg: 'RS256',
      typ: 'JWT',
    };

    const payload = {
      sub: `mock-${userId}`,
      ref_user_id: userId,
      email: email,
      iss: 'https://mock-auth.lore.local',
      aud: 'lore-web-client',
      exp: now + 3600, // 1 hour
      iat: now,
    };

    // Base64url encode (without padding for realism)
    const headerB64 = btoa(JSON.stringify(header)).replace(/=/g, '');
    const payloadB64 = btoa(JSON.stringify(payload)).replace(/=/g, '');
    const signature = 'mock-signature-' + Math.random().toString(36).substr(2, 9);

    return `${headerB64}.${payloadB64}.${signature}`;
  }

  /**
   * Generate consistent mock user ID from email
   */
  private generateMockUserId(email: string): string {
    // Simple hash for consistency
    let hash = 0;
    for (let i = 0; i < email.length; i++) {
      hash = ((hash << 5) - hash) + email.charCodeAt(i);
      hash = hash & hash;
    }
    return Math.abs(hash).toString();
  }

  /**
   * Store token in localStorage and CommunicationBridge
   */
  private storeToken(token: string, userId: string): void {
    localStorage.setItem(MockAuthService.STORAGE_KEY, token);
    localStorage.setItem(MockAuthService.USER_ID_KEY, userId);
    localStorage.setItem('access_token', token); // Also store in CommunicationBridge's key
    CommunicationBridge.setAccessToken(token);
  }
}

